import { cn } from '@/shared/lib/cn';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { Typography } from './typography';

type ToggleProps = {
  label?: string;
  leftLabel?: string;
  placement?: 'toggle-label' | 'label-toggle';
  fullWidth?: boolean;
  alwaysActive?: boolean;
  renderLabel?: (label: string) => ReactNode;
} & ComponentPropsWithoutRef<'input'>;

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      fullWidth = false,
      disabled = false,
      alwaysActive = false,
      placement = 'toggle-label',
      label,
      leftLabel,
      renderLabel,
      className,
      ...props
    },
    ref
  ) => {
    const toggleCore = (
      <div className="relative flex items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          ref={ref}
          disabled={disabled}
          {...props}
        />
        <div
          className={cn(
            'h-6 w-11 rounded-full bg-gray-200 transition-colors',
            'peer-focus:ring-primary-2 peer-focus:ring-4 peer-focus:outline-none',
            alwaysActive ? 'bg-primary-7' : 'peer-checked:bg-primary-7'
          )}
        />
        <span
          className={cn(
            'absolute top-[2px] left-[2px] h-5 w-5 rounded-full border border-gray-300 bg-white transition-transform',
            'peer-checked:translate-x-5'
          )}
        />
      </div>
    );

    return (
      <label
        className={cn(
          'flex cursor-pointer items-center gap-2',
          placement === 'toggle-label' && 'flex-row-reverse',
          fullWidth && 'w-full justify-between',
          disabled && 'cursor-not-allowed opacity-70',
          className
        )}
      >
        {label && (
          <Typography className={cn('select-none', disabled && 'opacity-50')}>
            {renderLabel ? renderLabel(label) : label}
          </Typography>
        )}

        {toggleCore}

        {leftLabel && (
          <Typography className={cn('select-none', disabled && 'opacity-50')}>
            {renderLabel ? renderLabel(leftLabel) : leftLabel}
          </Typography>
        )}
      </label>
    );
  }
);

Toggle.displayName = 'Toggle';
